/**
 * Personalized Treatment Options, client helpers + offline fallback.
 * Prefer POST /api/treatment-options/personalize; fall back locally if the API is unavailable.
 */
import { createApiClient } from '@/config/api';
import { cancerPagesData } from '@/data/cancerPagesData';
import { getPatientProfile } from '@/utils/patientProfile';

export const treatmentOptionsApi = createApiClient({ basePath: '/api/treatment-options' });

export const GOALS = [
  { id: 'cure_intent', label: 'Aim for curative treatment when possible' },
  { id: 'control', label: 'Control disease and buy time' },
  { id: 'symptoms', label: 'Ease symptoms and side effects' },
  { id: 'second_look', label: 'Get a second look before deciding' },
  { id: 'fertility', label: 'Protect fertility when relevant' },
  { id: 'work_life', label: 'Protect work, caregiving, or daily function' },
];

export const LINES_OF_CARE = [
  { id: 'newly_diagnosed', label: 'Newly diagnosed' },
  { id: 'on_treatment', label: 'Currently on treatment' },
  { id: 'recurrence', label: 'Recurrence / progression' },
  { id: 'survivorship', label: 'Survivorship / follow-up' },
  { id: 'unsure', label: 'Not sure' },
];

export const REGIONS = [
  { id: 'us', label: 'United States' },
  { id: 'canada', label: 'Canada' },
  { id: 'uk', label: 'United Kingdom' },
  { id: 'france', label: 'France' },
  { id: 'germany', label: 'Germany' },
  { id: 'japan', label: 'Japan' },
  { id: 'singapore', label: 'Singapore' },
  { id: 'china', label: 'China' },
  { id: 'india', label: 'India' },
  { id: 'other', label: 'Other / international' },
];

export const PERFORMANCE = [
  { id: 'fit', label: 'Mostly fit / independent' },
  { id: 'limited', label: 'Limited: needs help some days' },
  { id: 'frail', label: 'Frail: significant daily limits' },
  { id: 'unknown', label: 'Not sure' },
];

export const PRIOR_MODALITIES = [
  { id: 'surgery', label: 'Surgery' },
  { id: 'systemic', label: 'Chemo / targeted / immuno / hormone' },
  { id: 'radiation', label: 'Radiation' },
  { id: 'transplant', label: 'Transplant / cellular therapy' },
  { id: 'other', label: 'Other' },
];

export const STAGES = [
  { id: 'I', label: 'Stage I' },
  { id: 'II', label: 'Stage II' },
  { id: 'III', label: 'Stage III' },
  { id: 'IV', label: 'Stage IV' },
  { id: 'unknown', label: 'Stage not yet confirmed' },
];

const THEMES = [
  {
    id: 'surgery',
    title: 'Surgery-led care',
    summary:
      'Local control by removing the tumor when imaging and staging suggest it is resectable. Often paired with pathology review and adjuvant therapy.',
    fits: ['I', 'II', 'III'],
    clinic_tip: 'Ask whether margins, lymph nodes, reconstruction, and recovery time are part of the plan.',
  },
  {
    id: 'systemic',
    title: 'Systemic therapy',
    summary:
      'Medicines that travel through the body, chemotherapy, targeted therapy, immunotherapy, or hormone therapy, chosen from established classes for your cancer type.',
    fits: ['II', 'III', 'IV', 'unknown'],
    clinic_tip: 'Bring biomarker or genomic reports; they often decide the class of drug discussed.',
  },
  {
    id: 'radiation',
    title: 'Radiation therapy',
    summary:
      'Focused beams to shrink or sterilize disease in a defined field. Used alone, after surgery, or with systemic therapy depending on site and stage.',
    fits: ['I', 'II', 'III', 'IV'],
    clinic_tip: 'Clarify intent (curative vs palliative) and expected side effects for the treated field.',
  },
  {
    id: 'watchful',
    title: 'Active surveillance / watchful waiting',
    summary:
      'Close monitoring with exams and imaging when immediate treatment may not help, common in some early prostate cancers and selected indolent diseases.',
    fits: ['I', 'II', 'unknown'],
    clinic_tip: 'Agree on trigger points that would move you from watching to treating.',
  },
  {
    id: 'supportive',
    title: 'Supportive and palliative care',
    summary:
      'Symptom control, nutrition, pain, and emotional support alongside, or instead of, disease-directed therapy. Available at every stage.',
    fits: ['I', 'II', 'III', 'IV', 'unknown'],
    clinic_tip: 'Ask for a palliative or supportive-care referral early; it does not mean stopping treatment.',
  },
  {
    id: 'precision',
    title: 'Precision / biomarker-guided therapy',
    summary:
      'Using molecular results (receptors, drivers, MSI, PD-L1, germline findings) to narrow which drug classes are worth discussing with your team.',
    fits: ['I', 'II', 'III', 'IV', 'unknown'],
    clinic_tip: 'Confirm which markers were tested, which were not, and whether re-biopsy or liquid biopsy is useful.',
  },
  {
    id: 'trials',
    title: 'Clinical trials & emerging options',
    summary:
      'Research studies that may add access to newer approaches when standard pathways are incomplete, toxic, or exhausted, always weighed against travel, eligibility, and timing.',
    fits: ['II', 'III', 'IV', 'unknown'],
    clinic_tip: 'Ask which trial phase, what the control arm is, and how it affects standard care timing.',
  },
];

const DISCLAIMER =
  'ByOnco provides educational option themes to help you prepare for clinic conversations. Confirm every treatment decision with your oncology team.';

const SOURCES_NOTE =
  'Cancer-specific bullets come from ByOnco’s curated patient-education catalog. Theme ranking helps you explore common pathways before you talk with your clinician.';

/** Map profile / free text stage → I|II|III|IV|unknown */
export function normalizeStage(raw) {
  if (!raw) return 'unknown';
  const original = String(raw).trim();
  if (/not\s*diagnosed|unknown|undetermined|not\s*yet|n\/?a|pending/i.test(original)) {
    return 'unknown';
  }
  const s = original.toUpperCase();
  if (/\bIV\b|\b4\b|METASTATIC|STAGE\s*4/.test(s) || s.includes('STAGE IV')) return 'IV';
  if (/\bIII\b|\b3\b|STAGE\s*3/.test(s) || s.includes('STAGE III')) return 'III';
  if (/\bII\b|\b2\b|STAGE\s*2/.test(s) || s.includes('STAGE II')) return 'II';
  if (/\bI\b|\b1\b|STAGE\s*1|STAGE\s*0|\b0\b/.test(s) || s.includes('STAGE I')) return 'I';
  return 'unknown';
}

/** Map profile cancer type string toward a catalog-ish label */
export function normalizeCancerType(raw) {
  if (!raw) return 'Breast Cancer';
  const t = String(raw).trim();
  if (/breast/i.test(t)) return 'Breast Cancer';
  if (/lung|nsclc|sclc/i.test(t)) return 'Lung Cancer';
  if (/colorectal|colon|rectal/i.test(t)) return 'Colorectal Cancer';
  if (/prostate/i.test(t)) return 'Prostate Cancer';
  if (/pancrea/i.test(t)) return 'Pancreatic Cancer';
  if (/ovarian/i.test(t)) return 'Ovarian Cancer';
  if (/leukemia|leukaemia|blood/i.test(t)) return 'Leukemia';
  if (/lymphoma/i.test(t)) return 'Non-Hodgkin Lymphoma';
  if (/brain|glio/i.test(t)) return 'Brain Tumors (High-Grade Gliomas)';
  if (/oral|head|neck|throat/i.test(t)) return 'Oral Cancer';
  if (/melanoma/i.test(t)) return 'Melanoma';
  if (/cervical/i.test(t)) return 'Cervical Cancer';
  if (/liver|hcc/i.test(t)) return 'Liver Cancer';
  return t;
}

export function prefillFromProfile() {
  const profile = getPatientProfile();
  if (!profile) return null;
  const country = String(profile.country || '').toLowerCase();
  let region = 'us';
  if (/canada|\bca\b/.test(country)) region = 'canada';
  else if (/united kingdom|\buk\b|england|scotland|wales|\bgb\b/.test(country)) region = 'uk';
  else if (/france|\bfr\b/.test(country)) region = 'france';
  else if (/germany|\bde\b/.test(country)) region = 'germany';
  else if (/japan|\bjp\b/.test(country)) region = 'japan';
  else if (/singapore|\bsg\b/.test(country)) region = 'singapore';
  else if (/china|\bcn\b|prc/.test(country)) region = 'china';
  else if (/india|\bin\b/.test(country)) region = 'india';
  else if (/us|united states|usa/.test(country)) region = 'us';
  else if (country) region = 'other';
  return {
    cancerType: normalizeCancerType(profile.cancerType),
    stage: normalizeStage(profile.cancerStage),
    region,
  };
}

function cancerBias(name, stage) {
  const bias = Object.fromEntries(THEMES.map((t) => [t.id, 0]));
  const n = (name || '').toLowerCase();
  if (n.includes('prostate')) {
    bias.watchful += stage === 'I' || stage === 'II' || stage === 'unknown' ? 2.5 : 0.5;
    bias.radiation += 1.5;
    bias.surgery += 1.2;
  } else if (n.includes('breast')) {
    bias.surgery += ['I', 'II', 'III'].includes(stage) ? 2 : 0.5;
    bias.systemic += 1.8;
    bias.radiation += 1.2;
    bias.precision += 1.5;
    bias.watchful -= 1.5;
  } else if (n.includes('lung')) {
    bias.precision += 2.2;
    bias.systemic += 1.8;
    bias.surgery += ['I', 'II'].includes(stage) ? 1.5 : 0.3;
    bias.radiation += 1.2;
    bias.trials += 1;
  } else if (n.includes('leukemia') || n.includes('lymphoma')) {
    bias.systemic += 3;
    bias.precision += 1.5;
    bias.surgery -= 2;
  } else if (n.includes('pancreatic')) {
    bias.systemic += 2;
    bias.supportive += 1.2;
    bias.trials += 1.5;
    bias.watchful -= 2;
  }
  return bias;
}

function fitLabel(score, maxScore) {
  if (maxScore <= 0) return 'situational';
  const ratio = score / maxScore;
  if (ratio >= 0.72) return 'strong';
  if (ratio >= 0.45) return 'moderate';
  return 'situational';
}

export function rankThemesLocal({
  stage = 'unknown',
  goals = [],
  cancerLabel = '',
  biomarkers = [],
  lineOfCare = 'newly_diagnosed',
  priorModalities = [],
  performanceStatus,
} = {}) {
  const goalsSet = new Set(goals);
  const bias = cancerBias(cancerLabel, stage);
  const hasMarkers = biomarkers.some((m) => m && !/unknown|none|not.?tested/i.test(m));
  const prior = new Set(priorModalities);

  const scored = THEMES.map((theme) => {
    let score = theme.fits.includes(stage) ? 3 : 1;
    score += bias[theme.id] || 0;
    const why = [];
    if (theme.fits.includes(stage)) why.push(`commonly discussed at stage ${stage}`);
    if (cancerLabel) why.push(`aligned with typical ${cancerLabel} pathways`);
    if (goalsSet.has('cure_intent') && ['surgery', 'radiation', 'systemic', 'precision'].includes(theme.id)) {
      score += 2;
      why.push('matches curative-intent goals');
    }
    if (goalsSet.has('control') && ['systemic', 'radiation', 'precision', 'trials'].includes(theme.id)) {
      score += 2;
      why.push('supports disease-control goals');
    }
    if (goalsSet.has('symptoms') && theme.id === 'supportive') {
      score += 3;
      why.push('prioritizes symptom relief');
    }
    if (goalsSet.has('second_look')) {
      score += 0.8;
      if (['precision', 'trials'].includes(theme.id)) score += 0.6;
    }
    if (goalsSet.has('fertility') && ['surgery', 'systemic', 'radiation'].includes(theme.id)) score += 0.5;
    if (goalsSet.has('work_life') && ['watchful', 'supportive', 'radiation'].includes(theme.id)) score += 0.6;
    if (hasMarkers && theme.id === 'precision') {
      score += 2.5;
      why.push('biomarkers entered, precision pathway elevated');
    }
    if (hasMarkers && theme.id === 'systemic') score += 1;
    if (lineOfCare === 'recurrence' && ['systemic', 'trials', 'precision', 'supportive'].includes(theme.id)) score += 1.5;
    if (lineOfCare === 'survivorship' && theme.id === 'supportive') score += 2;
    if (prior.has(theme.id) && lineOfCare !== 'recurrence') score -= 0.8;
    if (stage === 'IV') {
      if (theme.id === 'watchful') score -= 2.5;
      if (['systemic', 'supportive', 'trials', 'precision'].includes(theme.id)) score += 1;
      if (theme.id === 'surgery') score -= 0.8;
    }
    if ((performanceStatus === 'frail' || performanceStatus === 'limited') && ['surgery', 'systemic'].includes(theme.id)) {
      score -= 1;
    }
    if ((performanceStatus === 'frail' || performanceStatus === 'limited') && theme.id === 'supportive') score += 1.5;
    return {
      ...theme,
      score: Math.max(0.2, Math.round(score * 100) / 100),
      why_ranked: [...new Set(why)].join('; ') || 'general oncology theme',
    };
  }).sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

  const maxScore = scored[0]?.score || 1;
  return scored.map((row, index) => ({
    id: row.id,
    title: row.title,
    summary: row.summary,
    why_ranked: row.why_ranked,
    clinic_tip: row.clinic_tip,
    rank: index + 1,
    score: row.score,
    fit: fitLabel(row.score, maxScore),
  }));
}

function seoOptionsForCancer(cancerLabel) {
  const pages = cancerPagesData || {};
  const key = Object.keys(pages).find((k) => {
    const title = pages[k]?.displayName || pages[k]?.name || k;
    const needle = String(cancerLabel).toLowerCase();
    return (
      String(title).toLowerCase().includes(needle.split(' ')[0]) ||
      needle.includes(String(k).toLowerCase())
    );
  });
  if (!key) return [];
  const page = pages[key];
  const options = page?.content?.treatment?.options || page?.treatment?.options || [];
  return options.map((o) => ({
    text: o.description ? `${o.name}: ${o.description}` : o.name || String(o),
    modality_hint: null,
  }));
}

export function personalizeLocal(input) {
  const cancerLabel = input.cancer_type || input.cancerType || 'your cancer type';
  const stage = input.stage || 'unknown';
  const goals = input.goals || ['second_look'];
  const biomarkers = input.biomarkers || [];
  const lineOfCare = input.line_of_care || input.lineOfCare || 'newly_diagnosed';
  const region = input.region || 'us';
  const themes = rankThemesLocal({
    stage,
    goals,
    cancerLabel,
    biomarkers,
    lineOfCare,
    priorModalities: input.prior_modalities || input.priorModalities || [],
    performanceStatus: input.performance_status || input.performanceStatus,
  });

  const cancer_specific_options = seoOptionsForCancer(cancerLabel);
  const missing_info = [];
  if (stage === 'unknown') {
    missing_info.push({
      field: 'stage',
      priority: 'high',
      why: 'Stage (TNM or I-IV) strongly shapes which themes are realistic to discuss first.',
    });
  }
  if (!biomarkers.length) {
    missing_info.push({
      field: 'biomarkers',
      priority: 'high',
      why: 'Molecular markers often change which systemic therapy classes belong in the conversation.',
    });
  }

  return {
    cancer_label: cancerLabel,
    catalog_matched: cancer_specific_options.length > 0,
    stage,
    goals,
    line_of_care: lineOfCare,
    region,
    themes,
    cancer_specific_options,
    molecular_markers: biomarkers,
    biomarker_notes: biomarkers.map((marker) => ({
      marker,
      note: 'Ask your team how this result changes drug class, sequencing, or trial eligibility.',
    })),
    india_context: null,
    clinic_questions: [
      `For ${cancerLabel} at stage ${stage}, which option themes are on your shortlist and why?`,
      `What would make you choose or avoid ${themes[0]?.title || 'the leading option'} in my case?`,
      'What is the goal of the first treatment block, cure, control, or comfort?',
      'Which tests are still missing before a final recommendation?',
      'What side effects should I plan for in the first 4-8 weeks?',
    ],
    shared_decision_checklist: [
      'Confirm diagnosis, stage, and pathology with the treating team',
      'List your top 2-3 personal priorities',
      'Ask for benefits, harms, and alternatives for each leading option',
      'Clarify timeline: what must start this week vs what can wait',
      'Identify the multidisciplinary team',
      'Plan practical supports: caregiver, travel, cost estimate',
    ],
    missing_info,
    clinical_trials_note:
      'Clinical trials can be appropriate at many points, not only after everything else fails. Ask about eligibility, phase, travel burden, and whether joining delays standard care.',
    multidisciplinary_note: `Optimal plans for ${cancerLabel} are usually set by a multidisciplinary team. Ask whether your case will be reviewed in a tumor board.`,
    side_effect_priorities: [
      'Ask which side effects are expected vs urgent red flags for your specific plan',
      'Nausea, infection risk, fatigue, and pain should have a clear after-hours plan',
    ],
    next_steps: [
      { id: 'second_opinion', label: 'Second-opinion brief', href: '/second-opinion', reason: 'Turn records into clinic-ready questions' },
      { id: 'journey', label: 'Build a care package', href: '/journey-builder', reason: 'Logistics and cost bands' },
      { id: 'teleconsult', label: 'Book a video consult', href: '/teleconsultation', reason: 'Talk through option themes' },
      { id: 'hospitals', label: 'Find hospitals', href: '/find-hospitals', reason: 'Centers experienced in your cancer family' },
      { id: 'cost', label: 'Estimate costs', href: '/cost-calculator', reason: 'Rough treatment cost bands' },
    ],
    disclaimer: DISCLAIMER,
    sources_note: SOURCES_NOTE,
    _source: 'local',
  };
}

export async function fetchCatalog() {
  const res = await treatmentOptionsApi.get('/catalog');
  if (!res.ok) throw new Error('catalog failed');
  return res.json();
}

export async function fetchSuggestedBiomarkers(cancerType) {
  const res = await treatmentOptionsApi.get(`/biomarkers?cancer_type=${encodeURIComponent(cancerType)}`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.markers || [];
}

export async function personalizeTreatmentOptions(payload) {
  try {
    const res = await treatmentOptionsApi.post('/personalize', payload);
    if (res.ok) {
      const data = await res.json();
      return { ...data, _source: 'api' };
    }
  } catch {
    // fall through
  }
  return personalizeLocal(payload);
}

export function buildClinicBriefText(result) {
  if (!result) return '';
  const lines = [
    `ByOnco clinic brief, ${result.cancer_label} · Stage ${result.stage}`,
    '',
    'Top option themes:',
    ...result.themes.slice(0, 5).map((t) => `${t.rank}. ${t.title}, ${t.why_ranked}`),
    '',
    'Questions to ask:',
    ...result.clinic_questions.map((q) => `• ${q}`),
    '',
    result.disclaimer,
  ];
  return lines.join('\n');
}
